import React, { useMemo } from "react";
import {
  Typography,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink, useHistory, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ROUTES } from "../../constants";
import { DynamicForm } from "../../dynamicForm/forms/DynamicForm";
import { useDynamicForm } from "../../dynamicForm/hooks/useDynamicForm";
import {
  newWellRegistrationFormConfig,
  adminWellRegistrationFormConfig,
  PURPOSE_OF_WELL_FIELDS,
} from "../../dynamicForm/formConfigs/newWellRegistrationFormConfig";
import existingWellRegistrationFormConfig, {
  PURPOSE_OF_EXEMPT_WELL_FIELDS,
} from "../../dynamicForm/formConfigs/existingWellRegistrationFormConfig";
import { usePendingWellRegistrations } from "./usePendingWellRegistrations";
import { useWellType } from "./useWellType";
import {
  ContentSkeleton,
  ErrorCard,
  PageContainer,
} from "../../dynamicForm/components/ui";
import PendingWellRegistrationsList from "./PendingWellRegistrationsList";

function validatePurposeOfWell(data) {
  const isExistingWell = data.is_existing_well;
  const fields = isExistingWell
    ? PURPOSE_OF_EXEMPT_WELL_FIELDS
    : PURPOSE_OF_WELL_FIELDS;
  const selected = fields.filter((k) => data[k]);
  if (selected.length !== 1) {
    return [
      {
        path: [selected[0] ?? fields[0]],
        message: "Select exactly one purpose type",
        type: "group",
      },
    ];
  }
  return [];
}

// Form component that receives well type from list data to avoid extra API calls
function WellRegistrationForm({ wellId, isExistingWell }) {
  const history = useHistory();
  const redirectPath = ROUTES.PENDING_WELL_REGISTRATIONS_MANAGEMENT;
  const handleRedirect = () => history.push(redirectPath);

  const formConfig = useMemo(
    () =>
      isExistingWell
        ? [
            ...adminWellRegistrationFormConfig,
            ...existingWellRegistrationFormConfig,
          ]
        : [
            ...adminWellRegistrationFormConfig,
            ...newWellRegistrationFormConfig,
          ],
    [isExistingWell]
  );

  const {
    isLoading: isLoadingFormData,
    error: formDataError,
    refetchFormData,
    refetchLookups,
    ...rest
  } = useDynamicForm({
    ndx: wellId,
    isNewRecord: false,
    config: formConfig,
    endpoints: {
      submit: `/well-registration`,
      fetch: `/well-registration`,
    },
    handleRedirect,
    onSuccess: () => {},
    customValidate: validatePurposeOfWell,
  });

  if (formDataError) {
    return (
      <ErrorCard
        onRetry={() =>
          Promise.all(
            [refetchFormData, refetchLookups].map((action) => action())
          )
        }
        isFetching={isLoadingFormData}
      />
    );
  }

  if (isLoadingFormData) {
    return <ContentSkeleton />;
  }

  return <DynamicForm config={formConfig} {...rest} />;
}

export default function PendingWellRegistrationPage() {
  const { id } = useParams();

  // Only fetch full list when showing the list (no id)
  const {
    data: listData,
    isFetching: isLoadingList,
    error: listError,
    refetch: refetchList,
  } = usePendingWellRegistrations(!id);

  // Only fetch well type when editing (has id)
  const {
    data: wellTypeData,
    isFetching: isLoadingWellType,
    error: wellTypeError,
  } = useWellType(id);

  const error = listError || wellTypeError;
  const isFetching = isLoadingList || isLoadingWellType;

  const pageTitle = id
    ? `Edit Well Registration #${id}`
    : "Pending Well Registrations";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Typography variant="h3" gutterBottom display="inline">
        {pageTitle}
      </Typography>
      <Breadcrumbs sx={{ mt: 4 }} aria-label="Breadcrumb">
        <MuiLink component={RouterLink} to={ROUTES.LANDING}>
          Home
        </MuiLink>
        <MuiLink component={RouterLink} to={ROUTES.PUBLIC_FORMS}>
          Forms
        </MuiLink>
        <Typography>{pageTitle}</Typography>
      </Breadcrumbs>
      <Divider sx={{ my: 6 }} />

      <PageContainer>
        {error ? (
          <ErrorCard onRetry={refetchList} isFetching={isFetching} />
        ) : isFetching ? (
          <ContentSkeleton />
        ) : id && wellTypeData ? (
          <WellRegistrationForm
            wellId={id}
            isExistingWell={wellTypeData.is_existing_well}
          />
        ) : id ? (
          <ErrorCard message="Well registration not found" />
        ) : (
          <PendingWellRegistrationsList registrations={listData || []} />
        )}
      </PageContainer>
    </>
  );
}
